import * as React from 'react'
import { ChevronRight } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import type { RecordHistoryDto } from '@lib/dto/site-history.dto'
import type { SiteChangeEntity } from '@lib/enums/site-change.enum'
import { describeChange, describeWork } from '@lib/helpers/describe-change'
import { siteVisitService } from '@/services/SiteVisitService'
import { siteVisitKeys } from '@/hooks/queryKeys'
import { StatusPill } from '@/components/StatusPill'
import { formatVisitDay } from '@/site-visits/VisitBar'
import { cn } from '@/lib/utils'

/** One line of a record's history: a visit's findings, a change, or work done. */
interface HistoryLine {
  key: string
  day: string
  text: string
  detail: string | null
  isCorrection: boolean
}

/** Every line, newest first. */
function linesOf(history: RecordHistoryDto): HistoryLine[] {
  const findings = history.findings.map((visit) => ({
    key: `visit-${visit.visitId}`,
    day: visit.startedOn,
    text:
      visit.problems.length === 0
        ? 'Visit · nothing found'
        : `Visit · ${visit.problems.join(', ')}`,
    detail: visit.work.length === 0 ? null : visit.work.join(', '),
    isCorrection: false,
  }))
  const changes = history.changes.map((change) => ({
    key: `change-${change.id}`,
    day: change.effectiveOn,
    text: describeChange(change, history.names),
    detail:
      change.job === null || change.job.number === null
        ? null
        : `Job ${change.job.number}`,
    isCorrection: change.isCorrection,
  }))
  const work = history.treeWork.map((done) => ({
    key: `work-${done.id}`,
    day: done.completedOn,
    text: describeWork(done),
    detail: `Work done${done.jobNumber === null ? '' : ` · Job ${done.jobNumber}`}`,
    isCorrection: false,
  }))
  return [...findings, ...changes, ...work].sort((a, b) =>
    b.day.localeCompare(a.day),
  )
}

/**
 * A record's own history at the foot of its sheet: what each visit found on
 * it, its changes (corrections muted), and work done on it, newest first
 * (docs/site-visits.md, "The site page"). Closed to start with, and read only
 * when opened, so a sheet opened in a park costs nothing; it needs a signal.
 */
export function RecordHistory({
  siteId,
  entity,
  recordId,
  controlClassName,
}: {
  siteId: string
  entity: SiteChangeEntity
  recordId: string
  controlClassName?: string | undefined
}) {
  const [open, setOpen] = React.useState(false)
  const query = useQuery({
    queryKey: siteVisitKeys.record(siteId, recordId),
    queryFn: () => siteVisitService.recordHistory(siteId, entity, recordId),
    enabled: open,
    retry: false,
  })

  function renderBody(): React.ReactNode {
    if (query.isPending) {
      return <p className="text-xs text-muted-text">Loading its history…</p>
    }
    if (query.isError) {
      return (
        <p role="alert" className="text-xs text-danger">
          Its history could not be loaded. It needs a signal.
        </p>
      )
    }
    const lines = linesOf(query.data)
    if (lines.length === 0) {
      return <p className="text-xs text-muted-text">Nothing recorded yet.</p>
    }
    return (
      <ul className="divide-y divide-default rounded-md border border-default bg-surface">
        {lines.map((line) => (
          <li key={line.key} className="space-y-0.5 px-2.5 py-1.5">
            <p className="flex items-baseline justify-between gap-2 text-sm">
              <span
                className={
                  line.isCorrection ? 'text-secondary-text' : undefined
                }
              >
                {line.text}
              </span>
              {line.isCorrection ? (
                <StatusPill tone="gray" className="shrink-0">
                  Correction
                </StatusPill>
              ) : null}
            </p>
            <p className="text-xs text-muted-text">
              {formatVisitDay(line.day)}
              {line.detail === null ? '' : ` · ${line.detail}`}
            </p>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <section className="rounded-md border border-default bg-subtle">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current)
        }}
        className={cn(
          'flex w-full items-center gap-1.5 px-2.5 text-left text-sm font-medium',
          controlClassName ?? 'h-11 lg:h-9',
        )}
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-text transition-transform',
            open ? 'rotate-90' : null,
          )}
        />
        History
      </button>
      {open ? <div className="px-2.5 pb-2.5">{renderBody()}</div> : null}
    </section>
  )
}
